import { clipBelongsToStory } from '@src/services/common/clipBelongsToStory';
import { isTempClipId } from '@src/services/WriteQueueService';
import { useClipPreview } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useEffect } from 'react';

import type { Video, VideoClip } from '../../../types.ts/story';

const logger = LogManager.getLogger('hook:useClipResourceLoader');

type ClipWithPreviewProps = VideoClip & {
  autoPlay?: boolean;
  videoSource?: Video['videoSource'];
  selectedDimension?: string;
};

/**
 * Loads clip resources (videoSource, transcription, karaoke, scenes) when
 * the modal opens and the clip doesn't yet have a videoSource.
 *
 * Delegates all fetching to ClipPreviewStore.prefetch so the same cache is
 * shared between hover pre-fetch and click-time loading.
 *
 * Covers all entry points (dashboard link, content-studio card click, etc.)
 * by living in the modal rather than the opener.
 */
export function useClipResourceLoader(clip: ClipWithPreviewProps) {
  const videoCreator = useVideoCreatorStore();
  const clipPreviewStore = useClipPreview();
  const story = videoCreator?.story;

  useEffect(() => {
    if (clip.videoSource || !clip.id || !story?.id) return;

    // Skip API fetch for temp clips (still being saved to Dato).
    // Their videoSource is generated locally via expandLightweightClipInBackground.
    if (isTempClipId(clip.id)) {
      logger.info(
        'Skipping resource fetch for temp clip — expansion handles it',
        {
          clipId: clip.id,
        },
      );
      return;
    }

    // Belt to the SocialClipsView mount gate: never fetch resources scoped to a
    // story the clip doesn't belong to. During the cross-navigation window the
    // global story still holds the previous page's story, and fetching with its
    // id would 403 (wrong storyId for this videoId). The effect re-runs when
    // story.id updates, so this resolves once the clip's story loads.
    if (!clipBelongsToStory(clip, story)) {
      return;
    }

    let cancelled = false;
    logger.info('Clip missing videoSource — loading resources', {
      clipId: clip.id,
      storyId: story.id,
    });

    const loadResources = async () => {
      try {
        const videoRepository = videoCreator.datoClientStore.videoRepository;

        // Always go through ClipPreviewStore — it deduplicates in-flight
        // requests and caches results for future opens of the same clip.
        const cached = await clipPreviewStore.prefetch(
          story.id,
          clip,
          videoRepository,
        );

        if (cancelled) return;

        const { populatedClip, extraElementData, associatedVideos } = cached;

        // Build the preview clip with all populated data
        const previewClip: VideoClip & {
          autoPlay?: boolean;
          selectedDimension?: string;
          extraElementData?: Video['extraElementData'];
        } = {
          ...populatedClip,
          extraElementData: extraElementData ?? {},
          associatedVideos: associatedVideos.length
            ? associatedVideos
            : populatedClip.associatedVideos,
        };

        // Determine selectedDimension — fallback to non-main published video
        if (!clip.videoFilePrimary && clip.associatedVideos?.length) {
          const nonMainVideo = clip.associatedVideos.find(
            v => v.videoFilePrimary,
          );
          if (nonMainVideo) {
            previewClip.selectedDimension = nonMainVideo.aspectRatio;
          }
        }

        if (!previewClip.selectedDimension) {
          previewClip.selectedDimension =
            clip.selectedDimension ||
            clip.aspectRatio ||
            videoCreator.getDefaultAspectRatio();
        }

        logger.info('Clip resources loaded, updating preview', {
          clipId: clip.id,
          associatedCount: associatedVideos.length,
        });
        videoCreator.setClipPreviewAndUpdateSource({
          ...previewClip,
          autoPlay: clip.autoPlay,
        });
      } catch (err) {
        if (cancelled) return;
        logger.error('Failed to load clip resources for preview', {
          clipId: clip.id,
          error: extractErrorInfo(err),
        });
      }
    };

    loadResources();

    return () => {
      cancelled = true;
    };
  }, [clip.id, clip.videoSource, story?.id]);
}
